/**
 * Shared notification display helpers — used by both the header NotificationBell
 * dropdown and the full /notifications page so routing, icons, and time
 * formatting stay consistent.
 */

import {
  Bell,
  Users,
  Trophy,
  AlertTriangle,
  ClipboardList,
  Mail,
  Calendar,
  BadgePercent,
  Check,
  X,
  Undo2,
} from 'lucide-react'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'

export interface CrmNotification {
  id: string
  user_id: string
  type: string
  title: string
  message?: string
  entity_type?: string
  entity_id?: string
  is_read: boolean
  created_date: string
}

const ENTITY_ROUTES: Record<string, string> = {
  task: '/tasks',
  lead: '/leads',
  opportunity: '/opportunities',
  account: '/accounts',
  contact: '/contacts',
  ticket: '/tickets',
  event: '/events',
  order: '/orders',
  quote: '/quotes',
  pricebook: '/pricebooks',
  // The backend sends entity_type 'contract_review' when a review changes hands.
  // Without this entry the notification appears but links nowhere.
  contract_review: '/contract-reviews',
}

// Some entities have a nested detail route rather than `${base}/${id}`. Map
// the entity type to the suffix appended after the id.
const ENTITY_ROUTE_SUFFIX: Record<string, string> = {
  pricebook: '/prices', // the notification is about entry changes → deep-link to the Manage Prices editor
}

/**
 * Resolve the in-app destination for a notification, or null when it isn't
 * deep-linkable (no entity, or an unmapped entity type).
 */
export function getNotificationLink(notification: CrmNotification): string | null {
  if (!notification.entity_type || !notification.entity_id) return null
  const basePath = ENTITY_ROUTES[notification.entity_type]
  if (!basePath) return null
  const suffix = ENTITY_ROUTE_SUFFIX[notification.entity_type] || ''
  // Discount notifications deep-link straight to the opportunity's Products
  // tab, where the discount + Approve/Endorse controls live.
  const query = notification.type.startsWith('discount_') ? '?tab=products' : ''
  return `${basePath}/${notification.entity_id}${suffix}${query}`
}

export function getNotificationIcon(type: string) {
  switch (type) {
    case 'assignment':
      return (
        <Users
          className="h-4 w-4"
          style={{ color: CATEGORICAL.evergreen.solid }}
          aria-hidden="true"
        />
      )
    case 'closed_won':
      return (
        <Trophy
          className="h-4 w-4"
          style={{ color: CATEGORICAL.evergreen.solid }}
          aria-hidden="true"
        />
      )
    case 'sla_breach':
    case 'email_failed':
      return (
        <AlertTriangle
          className="h-4 w-4"
          style={{ color: GRACKLE_COLORS.darkRaspberry }}
          aria-hidden="true"
        />
      )
    case 'discount_approval_needed':
    case 'discount_endorsed':
      return (
        <BadgePercent
          className="h-4 w-4"
          style={{ color: CATEGORICAL.clay.solid }}
          aria-hidden="true"
        />
      )
    case 'discount_approved':
      return (
        <Check
          className="h-4 w-4"
          style={{ color: CATEGORICAL.evergreen.solid }}
          aria-hidden="true"
        />
      )
    case 'discount_rejected':
      return (
        <X className="h-4 w-4" style={{ color: GRACKLE_COLORS.darkRaspberry }} aria-hidden="true" />
      )
    // A decision was reversed and the line is back to pending.
    case 'discount_approval_reverted':
      return (
        <Undo2 className="h-4 w-4" style={{ color: CATEGORICAL.clay.solid }} aria-hidden="true" />
      )
    case 'stage_change':
      return (
        <ClipboardList
          className="h-4 w-4"
          style={{ color: CATEGORICAL.plum.solid }}
          aria-hidden="true"
        />
      )
    case 'comment':
      return (
        <Mail className="h-4 w-4" style={{ color: CATEGORICAL.clay.solid }} aria-hidden="true" />
      )
    case 'due_today':
    case 'due_tomorrow':
    case 'overdue':
      return (
        <Calendar
          className="h-4 w-4"
          style={{ color: CATEGORICAL.clay.solid }}
          aria-hidden="true"
        />
      )
    case 'ticket_resolved':
    case 'ticket_closed':
      return (
        <Check
          className="h-4 w-4"
          style={{ color: CATEGORICAL.evergreen.solid }}
          aria-hidden="true"
        />
      )
    default:
      return <Bell className="h-4 w-4 text-foreground" aria-hidden="true" />
  }
}

export function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
